import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import BookingStrip from './BookingStrip';
import JourneyProgress from './JourneyProgress';
import FeedbackSection from './FeedbackSection';
import ThankYouScreen from './ThankYouScreen';
import { API_BASE_URL, API_ENDPOINTS } from '../api';
import logo from "../images/logo-rg-1.webp"; 


const STEPS = [
  { id: 'pickup', label: 'Pickup', ico: '' },
  { id: 'counter', label: 'Counter', ico: '' },
  { id: 'vehicle', label: 'Vehicle', ico: '' },
  { id: 'dropoff', label: 'Drop-off', ico: '' }
];

export default function FeedbackPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);
  const [shuttleUsed, setShuttleUsed] = useState(false);
  const [responses, setResponses] = useState({
    pickup: { stars: 0, tags: [], comment: "" },
    counter: { stars: 0, tags: [], comment: "" },
    vehicle: { stars: 0, tags: [], comment: "" },
    dropoff: { stars: 0, tags: [], comment: "" },
  });
  const [bookingData, setBookingData] = useState({ resNo: "", supplier: "", car: "" });
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [shuttleResponse, setShuttleResponse] = useState({
    stars: 0,
    tags: [],
    comment: ""
  });

  // Final Review States
  const [isFinalReview, setIsFinalReview] = useState(false);
  const [finalOverallComment, setFinalOverallComment] = useState("");

  const token = searchParams.get('token');
  const mood = searchParams.get('mood');

  // --- LOGIC HELPERS ---
  const totalStars = Object.values(responses).reduce((acc, curr) => acc + curr.stars, 0);
  const isFinalCommentMandatory = totalStars < 6;
  const currentData = responses[STEPS[step - 1].id];
  const isStepValid = currentData.stars > 0;

  // --- 1. VALIDATION API ---
  useEffect(() => {
    if (!token) {
      setIsValid(false);
      setLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}${API_ENDPOINTS.FEEDBACK_VALIDATION}?token=${token}&mood=${mood}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.already_submitted === true) {
          setIsAlreadySubmitted(true);
          setIsValid(false);
        } else if (data.valid) {
          setIsValid(true);
          setBookingData({
            resNo: data.reservation_no || "N/A",
            supplier: data.supplier || "N/A",
            vehicle: data.vehicle || "N/A",
          });
        } else {
          setIsValid(false);
        }
      })
      .catch(() => setIsValid(false))
      .finally(() => setLoading(false));
  }, [token, mood]);

  // --- 2. SUBMISSION LOGIC ---
  const handleFinalSubmit = () => {
    setLoading(true);
    const payload = {
      token: token,
      mood: mood,
      shuttle_used: shuttleUsed,
      final_overall_comment: finalOverallComment,
      total_stars_sum: totalStars,
      shuttle: {
        stars_given: shuttleResponse.stars,
        tags_selected: shuttleResponse.tags,
        comment: shuttleResponse.comment
      },
      pickup: {
        stars_given: responses.pickup.stars,
        tags_selected: responses.pickup.tags,
        comment: responses.pickup.comment
      },
      counter: {
        stars_given: responses.counter.stars,
        tags_selected: responses.counter.tags,
        comment: responses.counter.comment
      },
      vehicle: {
        stars_given: responses.vehicle.stars,
        tags_selected: responses.vehicle.tags,
        comment: responses.vehicle.comment
      },
      dropoff: {
        stars_given: responses.dropoff.stars,
        tags_selected: responses.dropoff.tags,
        comment: responses.dropoff.comment
      }
    };

    fetch(`${API_BASE_URL}${API_ENDPOINTS.FEEDBACK_SUBMISSION}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => res.ok && setSubmitted(true))
      .finally(() => setLoading(false));
  };

  // --- 3. UI STATES ---
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center font-body text-text">
        Loading...
      </div>
    );
  }
  if (isAlreadySubmitted) {
    return (
      <div className="h-screen flex items-center justify-center font-body text-center p-4 text-text">
        You have already submitted feedback for this trip. Thank you!
      </div>
    );
  }
  if (!isValid) {
    return (
      <div className="h-screen flex items-center justify-center font-body text-center p-4 text-text">
        This link has expired or is invalid.
      </div>
    );
  }
  if (submitted) return <ThankYouScreen name="" />;

  return (
    <div className="min-h-screen bg-surface-raised py-10 px-4 font-body text-text">
      <div className="mx-auto mt-12 max-w-[580px] overflow-hidden rounded-lg border border-surface-border bg-surface shadow-card">

        {/* HEADER */}
        <header className="bg-dark p-8 pb-0">
          {/* LOGO CONTAINER */}
          <div className="flex items-center group mb-4">
            <img 
              src={logo} 
              alt="Rent Gurus" 
              className="h-11 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </div>

          <h1 className="font-heading text-3xl font-extrabold leading-tight text-white pb-6">
            Share your trip <br /><span className="text-primary">experience.</span>
          </h1>
        </header>

        <div className="p-7">
          <BookingStrip resNo={bookingData.resNo} supplier={bookingData.supplier} vehicle={bookingData.vehicle} />

          {!isFinalReview ? (
            <>
              <JourneyProgress steps={STEPS} currentStep={step} />

              {/* Step 1 Shuttle Toggle */}
              {step === 1 && (
                <>
                  <div className="mb-4 flex items-center justify-between rounded-lg border border-primary/20 bg-accent p-3">
                    <p className="text-xs font-bold text-accent-foreground">Did you use a shuttle?</p>
                    <div className="flex gap-1 rounded-full border border-surface-border bg-surface p-1">
                      <button
                        onClick={() => setShuttleUsed(false)}
                        className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all ${!shuttleUsed ? 'bg-dark text-white' : 'text-text-muted'}`}
                      >
                        No
                      </button>
                      <button
                        onClick={() => setShuttleUsed(true)}
                        className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all ${shuttleUsed ? 'bg-dark text-white' : 'text-text-muted'}`}
                      >
                        Yes
                      </button>
                    </div>
                  </div>

                  {/* Conditionally reveal Shuttle Feedback */}
                  {shuttleUsed && (
                    <div className="mb-4 transition-all duration-300 ease-in-out">
                      <FeedbackSection
                        type="shuttle"
                        label="Shuttle Experience"
                        icon=""
                        data={shuttleResponse}
                        onUpdate={(newData) => setShuttleResponse(prev => ({ ...prev, ...newData }))}
                      />
                    </div>
                  )}
                </>
              )}

              <FeedbackSection
                key={STEPS[step - 1].id}
                type={STEPS[step - 1].id}
                label={STEPS[step - 1].label}
                icon={STEPS[step - 1].ico}
                data={responses[STEPS[step - 1].id]}
                onUpdate={(newData) => setResponses(prev => ({
                  ...prev,
                  [STEPS[step - 1].id]: { ...prev[STEPS[step - 1].id], ...newData }
                }))}
              />

              {/* Navigation Buttons */}
              <div className="mt-4 flex gap-2">
                {step > 1 && (
                  <button
                    onClick={() => setStep(s => s - 1)}
                    className="rounded-full border border-surface-border px-5 py-2 text-sm font-semibold text-text hover:border-dark transition-all"
                  >
                    ← Back
                  </button>
                )}
                <button
                  onClick={() => step === 4 ? setIsFinalReview(true) : setStep(s => s + 1)}
                  disabled={!isStepValid}
                  className={`flex-1 rounded-full py-3 font-heading text-sm font-bold transition-all ${isStepValid
                      ? 'bg-primary text-white hover:bg-primary-hover hover:text-surface'
                      : 'bg-surface-border text-text-muted cursor-not-allowed'
                    }`}
                >
                  {step === 4 ? 'Finish' : 'Next Step'} →
                </button>
              </div>
            </>
          ) : (
            /* FINAL REVIEW INTERFACE */
            <div className="animate-fadeIn">
              <h3 className="font-heading font-bold text-lg text-text mb-2">Final Review</h3>
              <p className="text-xs text-text-muted mb-4">
                {isFinalCommentMandatory
                  ? "We noticed your overall experience was low. Please let us know what we can improve (Required)."
                  : "Anything else you'd like to share? (Optional)"}
              </p>

              <textarea
                value={finalOverallComment}
                onChange={(e) => setFinalOverallComment(e.target.value)}
                className={`w-full h-32 p-4 border rounded-md text-sm outline-none transition-all ${(isFinalCommentMandatory && !finalOverallComment.trim())
                    ? 'border-destructive bg-destructive/5 focus:border-destructive'
                    : 'border-surface-border focus:border-dark bg-surface text-text'
                  }`}
                placeholder="Your final thoughts..."
              />

              <button
                onClick={handleFinalSubmit}
                disabled={isFinalCommentMandatory && finalOverallComment.trim().length < 5}
                className={`w-full mt-4 py-3 rounded-full font-bold transition-all ${!(isFinalCommentMandatory && finalOverallComment.trim().length < 5)
                    ? 'bg-primary text-white hover:bg-primary-hover hover:text-surface'
                    : 'bg-surface-border text-text-muted cursor-not-allowed'
                  }`}
              >
                Submit All Feedback
              </button>
              <button
                onClick={() => setIsFinalReview(false)}
                className="w-full mt-2 text-xs text-text-muted hover:text-text underline transition-all"
              >
                Go back to sections
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}